/**
 * PHI primitives shared by the facade (track.ts) and the SDK backstop
 * (client.ts). Kept in a zero-dependency module so neither imports the other
 * (ADR-003). This is the authoritative definition of "what looks like PHI".
 */

/**
 * Property keys matching this are dropped from every payload — even when
 * allow-listed (defense in depth). The repo uses snake_case, so `remark_label`
 * (with separator) must be caught as well as `remarkLabel`; `remark_id` passes.
 */
export const PHI_KEY_DENYLIST =
  /(name|phone|mobile|email|address|uhid|dob|note|remark[_-]?label)/i;

/** Max string-value length; longer strings are truncated so free text can't hide in an id field. */
export const MAX_PROP_VALUE_LENGTH = 128;

/** True if a property key looks like PHI and must never be sent. */
export function isPhiKey(key: string): boolean {
  return PHI_KEY_DENYLIST.test(key);
}

/**
 * Deeply scans an arbitrary payload for any PHI-looking key (the SDK-boundary
 * backstop). Bounded depth guards against pathological/cyclic structures.
 */
export function deepContainsPhiKey(value: unknown, depth = 0): boolean {
  if (value === null || typeof value !== "object" || depth > 5) {
    return false;
  }
  for (const [key, nested] of Object.entries(value)) {
    if (isPhiKey(key)) {
      return true;
    }
    if (deepContainsPhiKey(nested, depth + 1)) {
      return true;
    }
  }
  return false;
}
